import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  zIndex?: number;
  /** Classes extras no corpo do modal (ex.: overflow-hidden para layout interno com scroll). */
  contentClassName?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  zIndex = 100,
  contentClassName,
}) => {
  const titleId = useId();

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
  };

  return createPortal(
    <div className="fixed inset-0 overflow-y-auto" style={{ zIndex }} role="presentation">
      {/** Sempre centralizado (viewport), inclusive em mobile — evita “folha” colada à topbar */}
      <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-6">
        <div
          className="fixed inset-0 bg-foreground/40 backdrop-blur-[2px] transition-opacity"
          onClick={onClose}
          aria-hidden
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={cn(
            'relative z-[1] my-auto flex w-full max-h-[min(90dvh,48rem)] flex-col overflow-hidden text-left',
            'transform rounded-card bg-card text-card-foreground shadow-soft-xl transition-all duration-200',
            sizeClasses[size]
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border/70 px-6 pb-4 pt-6">
            <h3
              className="min-w-0 flex-1 pr-2 font-heading text-heading-3 font-semibold text-foreground"
              id={titleId}
            >
              {title}
            </h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-9 w-9 shrink-0 rounded-input"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-6 py-4',
              contentClassName
            )}
            role="document"
          >
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
